// @ts-nocheck
import withDynamicGoogleMaps, { HasDynamicGoogleMaps } from "@insite/client-framework/Common/withDynamicGoogleMaps";
import { getUrl } from "@insite/client-framework/ServerSideRendering";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import constants from "@store/constants";
import { getReportByUrl, shareEmail, ShareEmailInterface } from "@store/Framework/Services/CoolCalcService";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { ThemeProps, withTheme } from "styled-components";

interface OwnProps extends WidgetProps {
    useLoadScript: ({ googleMapsApiKey, libraries }: { googleMapsApiKey: any; libraries?: string[] }) => {
        isLoaded: boolean;
    };
}
const mapStateToProps = (state: ApplicationState) => {
    const { userName } = state.context.session;
    const currentUserIsGuest = getCurrentUserIsGuest(state);

    return {
        settings: getSettingsCollection(state),
        currentBillTo: getCurrentBillToState(state).value,
        isSignedIn: userName && !currentUserIsGuest,
        isAuthenticated: state.context.session.isAuthenticated,
        isAdmin: state.context.session.userRoles?.toLowerCase().indexOf("administrator") !== -1,
        userName,
        signInUrl: getPageLinkByPageType(state, constants.pageType.signIn)?.url,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    loadCurrentBillTo,
};

type Props = OwnProps &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasDynamicGoogleMaps;

export interface CoolCalcStyles {
    widgeTitle?: TypographyProps;
}

const CoolCalc = ({
    settings,
    currentBillTo,
    isSignedIn,
    isAuthenticated,
    isAdmin,
    userName,
    useLoadScript,
    history,
    signInUrl,
    location,
}: Props) => {
    const [scriptsLoaded, setScriptsLoaded] = React.useState(false);
    const [displayReport, setDisplayReport] = React.useState(false);
    const { googleMapsApiKey } = settings.websiteSettings;
    const { isLoaded } = useLoadScript({ googleMapsApiKey, libraries: constants.googleMapsLibraries });
    const url: { protocol: string; host: string } = IS_SERVER_SIDE ? getUrl()! : window.location;

    const myCoolcalcConfig = {
        APIDomain: "api3.coolcalc.com",
        localDomain: url.host.toLowerCase(),
        APIClientEndpoint: "/api/v1/coolcalc",
        khipuConfigURL: "https://cdn.coolcalc.com/config/mj8/v-bootstrap/current/khipu-js.json",
        mapTraceConfigURL: "https://cdn.coolcalc.com/config/map-trace/current/map-trace.json",
        sessionUserURL: "",
        signInUrl,
        MJ8ReportURL: "",
        MJ8AjaxURL: "/api/v1/coolcalc",
        SVGIconURL: "/coolcalc/local-assets/sprites/coolcalc-resources.svg#",
    };

    React.useEffect(() => {
        if (isAuthenticated && isSignedIn) {
            if (!currentBillTo) {
                loadCurrentBillTo();
            }
        }
    }, []);

    React.useEffect(() => {
        if (!isLoaded) {
            return;
        }

        loadStyleSheetsArray(constants.coolCalcStylesheets);
        loadScriptsArray(constants.coolCalcFirstPassScripts)
            .then(() => {
                loadScriptsArray(constants.coolCalcSecondPassScripts)
                    .then(() => {
                        setScriptsLoaded(true);
                        setTimeout(() => {
                            initializeCoolCalc();
                        }, 500);
                    })
                    .catch(error => {
                        global.console.error("Script loading failed:", error);
                    });
            })
            .catch(error => {
                global.console.error("Script loading failed:", error);
            });

        return () => {
            const allScripts = [...constants.coolCalcFirstPassScripts, ...constants.coolCalcSecondPassScripts];
            unloadScripts(allScripts);
            unloadStylesheets(constants.coolCalcStylesheets);
        };
    }, [isLoaded]);

    const unloadScripts = (scripts: string[]) => {
        scripts.forEach((src: string) => {
            const scriptElement = document.querySelector(`script[src="${src}"]`);
            if (scriptElement) {
                document.body.removeChild(scriptElement);
            }
        });
    };

    const unloadStylesheets = (stylesheets: string[]) => {
        stylesheets.forEach((src: string) => {
            const stylesheetElement = document.querySelector(`link[href="${src}"]`);
            if (stylesheetElement) {
                document.body.removeChild(stylesheetElement);
            }
        });
    };

    const loadScript = (src: string) => {
        return new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = src;
            script.async = false;
            script.onload = () => resolve(src);
            script.onerror = () => reject(new Error(`Unable to load script: ${src}`));

            document.body.appendChild(script);
        });
    };

    const loadStylesheet = (href: string) => {
        return new Promise((resolve, reject) => {
            const link = document.createElement("link");
            link.href = href;
            link.type = "text/css";
            link.rel = "stylesheet";
            link.onload = () => resolve(href);
            link.onerror = () => reject(new Error(`Unable to load stylesheet: ${href}`));

            document.body.appendChild(link);
        });
    };

    const loadScriptsArray = (scriptsArray: any) => {
        return Promise.all(scriptsArray.map((src: string) => loadScript(src)));
    };

    const loadStyleSheetsArray = (scriptsArray: any) => {
        return Promise.all(scriptsArray.map((src: string) => loadStylesheet(src)));
    };

    const manageEmail = (someURL: string, email: string, backToCoolCalc: any) => {
        const reportUrl = (window as any).reactCoolcalcReportLink;
        const urlAddress = `${url.protocol.toLowerCase()}//${url.host.toLowerCase()}`;
        const completeUrl = `${urlAddress}/CoolCalc?report_url=${encodeURI(reportUrl)}`;

        shareEmail({
            ReportUrl: completeUrl,
            Email: email,
        }).then((response: any) => {
            backToCoolCalc();
        });
        return true;
    };

    const initializeCoolCalc = () => {
        const coolcalc = (global as any)["coolcalc"];
        const params = new URLSearchParams(location.search);
        const reportUrl = params.get("report_url") || params.get("report_id") || params.get("reportId");
        if (reportUrl) {
            showReport(reportUrl);
        } else {
            if (!isAuthenticated && !isSignedIn) {
                history.push(
                    `${myCoolcalcConfig.signInUrl}?returnUrl=${encodeURIComponent(
                        location.pathname + location.search,
                    )}&clientRedirect=true`,
                );
                return;
            }

            hookReportRenderHandler();

            const funFactory = {
                PDFHandler: () => {
                    return () => {};
                },
                emailHandler: () => {
                    return manageEmail;
                },
                CSRFHandler: () => {},
                startupCallback: () => {},
                renderHandlers: () => {},
            };
            const myUserInfo = {
                dealerReference: currentBillTo?.customerNumber,
                userReference: userName,
                isAdmin,
            };
            coolcalc.MJ8.projectList(myCoolcalcConfig, myUserInfo, funFactory, funFactory.startupCallback());
        }
    };

    const showReport = (reportUrl: string) => {
        setDisplayReport(true);
        const coolcalc = (global as any)["coolcalc"];

        getReportByUrl(reportUrl).then((response: any) => {
            // Initialize a Khipu instance, for no reason other than to use its HTML table rendering functions.
            // eslint-disable-next-line new-cap
            const restUI = new (global as any)["khipu"](
                { DOMElementId: "mj8-report", CSS: { hiddenContent: "d-none" } },
                null,
            );

            const myReport = response;
            coolcalc.renderMJ8Report(document.getElementById("mj8-report"), "", myReport, "MJ8Report", {}, restUI);

            document.title = `${myReport.MJ8Report.MJ8ReportId}.pdf`;

            const params = new URLSearchParams(location.search);
            const print = params.get("print");
            if (print) {
                (global as any)["google"].charts.setOnLoadCallback(() => {
                    (global as any).print();
                });
            }
        });
    };

    const hookReportRenderHandler = () => {
        const coolcalc = (global as any)["coolcalc"];
        const mj8ReportCallBackBackup = coolcalc.MJ8ReportRenderHandler;
        coolcalc.MJ8ReportRenderHandler = (
            resourceBodyDiv: any,
            responseHeaders: any,
            someResource: any,
            resourceName: any,
            options: any,
            restUI: any,
        ) => {
            mj8ReportCallBackBackup(resourceBodyDiv, responseHeaders, someResource, resourceName, options, restUI);

            let reportLInk = "";
            for (let indx = 0; indx < someResource.links.length; indx++) {
                if (someResource.links[indx].rel === "MJ8Report") {
                    const coolcalc = (global as any)["coolcalc"];

                    // Parsing URL with the worst ugly I have ever seen just because TypeScript version to old to just use new URL(href).
                    const href = someResource.links[indx].href;
                    const substring =
                        href.substring(0, href.lastIndexOf(coolcalc.extraInfo.APIDomain)) +
                        coolcalc.extraInfo.APIDomain;
                    const urlPath = href.replace(substring, "");

                    reportLInk = myCoolcalcConfig.MJ8AjaxURL + urlPath;
                }
            }
            (window as any).reactCoolcalcReportLink = reportLInk; // We also need that for sharing emails

            const btnDownloadPDF = resourceBodyDiv.querySelectorAll('[data-coolcalc-role="download-pdf"]')[0];
            if (btnDownloadPDF) {
                btnDownloadPDF.onclick = () => {
                    const urlAddress = `${url.protocol.toLowerCase()}//${url.host.toLowerCase()}`;
                    global.open(`${urlAddress}/CoolCalc?report_url=${encodeURI(reportLInk)}&print=1`, "_blank");
                };
            }

            // Quick patch to make sure coolcalc is not changing the URL in the browser address bar when clicking on reports tab
            (global as any).jQuery(".nav-link").click((event: any) => {
                event.preventDefault();
            });
        };

        coolcalc.resourceRenderCallbacks.MJ8Report = (global as any)["coolcalc"].MJ8ReportRenderHandler;
    };

    if (!scriptsLoaded) {
        return (
            <div className="coolcalc-widget block">
                <div className="block-wrapper">
                    <div id="coolcalc-ui" className="coolcalc-restui">
                        <div className="contains-spinner">
                            <span className="spinner">
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div>
                {!displayReport && (
                    <div className="coolcalc-widget block">
                        <div className="block-wrapper">
                            <div id="coolcalc-ui" className="coolcalc-restui">
                                <div className="contains-spinner">
                                    <span className="spinner">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {displayReport && (
                    <div className="coolcalc-widget mj8-report">
                        {/* <!-- MJ8 Report Resource content--> */}
                        <div className="mj8-report" id="mj8-report">
                            <div id="cc-overview" className="cc-page-break">
                                <div className="overview p-3 mb-3">
                                    {/* <!-- User Info --> */}
                                    <div className="d-flex justify-content-between">
                                        <div className="ml-auto logo">
                                            <img
                                                src="https://cdn.coolcalc.com/images/logo-report.png"
                                                alt="Cool Calc"
                                                title="Cool Calc Manual J"
                                            />
                                        </div>
                                    </div>
                                    {/* <!-- Project Info --> */}
                                    <h1 id="project-name" className="project">
                                        #
                                    </h1>
                                    <span id="address" className="address"></span>
                                </div>

                                {/* <!-- Design Conditions --> */}
                                <div className="card card-body my-4">
                                    <h3 className="title font-weight-bold mb-0">OUTDOOR DESIGN CONDITIONS</h3>
                                    <div className="underline"></div>

                                    <div className="mt-3">
                                        <div className="card">
                                            <div className="card-header">
                                                <div>
                                                    {" "}
                                                    <b>Weather station: </b> <span id="weatherStation"></span>
                                                </div>
                                            </div>
                                            <div className="table-full-width">
                                                <table className="table table-sm">
                                                    <tbody>
                                                        <tr>
                                                            <td>
                                                                <span className="">Summer Outdoor F</span>
                                                            </td>
                                                            <td className="text-right">
                                                                <span
                                                                    className="dc1 badge badge-primary"
                                                                    id="temSummerOutdoor"
                                                                ></span>
                                                            </td>
                                                            <td>
                                                                <span className="">Summer Indoor F</span>
                                                            </td>
                                                            <td className="text-right">
                                                                <span
                                                                    className="dc1 badge badge-primary"
                                                                    id="temSummerIndoor"
                                                                ></span>
                                                            </td>
                                                            <td>
                                                                <span className="">Design Grains</span>
                                                            </td>
                                                            <td className="text-right">
                                                                <span
                                                                    className="dc1 badge badge-primary"
                                                                    id="designGrains"
                                                                ></span>
                                                            </td>
                                                            <td>
                                                                <span className="">Daily Range</span>
                                                            </td>
                                                            <td className="text-right">
                                                                <span
                                                                    className="dc1 badge badge-primary"
                                                                    id="dailyRange"
                                                                ></span>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>
                                                                <span className="">Winter Outdoor F</span>
                                                            </td>
                                                            <td className="text-right">
                                                                <span
                                                                    className="dc2 badge badge-primary"
                                                                    id="temWinterOutdoor"
                                                                ></span>
                                                            </td>

                                                            <td>
                                                                <span className="">Winter Indoor F</span>
                                                            </td>
                                                            <td className="text-right">
                                                                <span
                                                                    className="dc2 badge badge-primary"
                                                                    id="temWinterIndoor"
                                                                ></span>
                                                            </td>

                                                            <td>
                                                                <span className="">Cooling RH</span>
                                                            </td>
                                                            <td className="text-right">
                                                                <span
                                                                    className="dc2 badge badge-primary"
                                                                    id="coolingRH"
                                                                ></span>
                                                            </td>
                                                            <td>
                                                                <span className="">Elevation (Ft)</span>
                                                            </td>
                                                            <td className="text-right">
                                                                <span
                                                                    className="dc2 badge badge-primary"
                                                                    id="elevation"
                                                                ></span>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* <!-- Totals --> */}
                                <div className="card card-body mb-4">
                                    <h3 className="title font-weight-bold mb-0">LOAD CALCULATION TOTALS</h3>
                                    <div className="underline"></div>

                                    <div className="mt-3">
                                        <div className="card mb-3">
                                            <div className="card-header">
                                                <div>
                                                    {" "}
                                                    <b>HVAC System: </b> <span id="hvac-system-name"></span>
                                                </div>
                                            </div>
                                            <div className="table-full-width">
                                                <table className="table">
                                                    <tbody>
                                                        <tr>
                                                            <td className="text-right">
                                                                <span className="">Heated square footage</span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="badge badge-primary"
                                                                    id="heatedFloorArea"
                                                                ></span>
                                                            </td>

                                                            <td className="text-right">
                                                                <span className="">Heating BTUH</span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="badge badge-primary"
                                                                    id="heatingBTUH"
                                                                ></span>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-right">
                                                                <span className="">Cooled square footage</span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="badge badge-primary"
                                                                    id="cooledFloorArea"
                                                                ></span>
                                                            </td>

                                                            <td className="text-right">
                                                                <span className="">Cooling BTUH</span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="badge badge-primary"
                                                                    id="coolingBTUH"
                                                                ></span>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-right">
                                                                <span className="">Heated volume (above grade CF)</span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="badge badge-primary"
                                                                    id="heatedAGV"
                                                                ></span>
                                                            </td>

                                                            <td className="text-right">
                                                                <span className="">CFM</span>
                                                            </td>
                                                            <td>
                                                                <span className="badge badge-primary" id="CFM"></span>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-right">
                                                                <span className="">Cooled volume (above grade CF)</span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="badge badge-primary"
                                                                    id="cooledAGV"
                                                                ></span>
                                                            </td>

                                                            <td className="text-right">
                                                                <span className="">Sensible cooling</span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="badge badge-primary"
                                                                    id="sensibleBTUH"
                                                                ></span>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-right">
                                                                <span className="">Exposed wall area (SF)</span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="badge badge-primary"
                                                                    id="exposedWallArea"
                                                                ></span>
                                                            </td>

                                                            <td className="text-right">
                                                                <span className="">Latent cooling</span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="badge badge-primary"
                                                                    id="latentBTUH"
                                                                ></span>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td></td>
                                                            <td></td>
                                                            <td className="text-right">
                                                                <span className="">SHR</span>
                                                            </td>
                                                            <td>
                                                                <span className="badge badge-primary" id="SHR"></span>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* <!-- BTUH chart --> */}
                                        <div className="row mt-3">
                                            <div className="col-md-10 offset-md-2">
                                                <div className="" data-coolcalc-role="btuh-chart"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* <!--ACCA detail--> */}
                                <div className="card card-body mb-4">
                                    <div className="row">
                                        <div className="col-md-8">
                                            <h3 className="title font-weight-bold mb-0">
                                                APPROVED ACCA MJ8 CALCULATIONS
                                            </h3>
                                            <div className="underline"></div>

                                            <p className="text-justify mt-2 ml-2">
                                                Calculations are based on the ACCA Manual J 8th Edition and are approved
                                                by ACCA. All computed calculations are estimates on building use,
                                                weather data, and inputted values such a R-Values, window types, duct
                                                loss, etc. Equipment selections should meet both the latent and sensible
                                                gain as well as building heat loss.
                                            </p>
                                        </div>
                                        <div className="col-md-4 text-center align-items-center d-flex">
                                            <div className="acca w-100">
                                                <img
                                                    src="https://cdn.coolcalc.com/images/PoweredbyACCAManualJ.png"
                                                    alt="Powered by ACCA"
                                                />
                                            </div>
                                        </div>
                                        {/* <!--div className="col-md-4"> */}
                                        <div id="qrcode" className=""></div>
                                        {/* </div--> */}
                                    </div>
                                </div>

                                <div className="cc-page-break">
                                    <div className="text-center">
                                        <h3 className="title font-weight-bold mb-0 mt-2">HEATING AND COOLING LOADS</h3>
                                    </div>

                                    {/* <!-- Heating --> */}
                                    <h3 className="title font-weight-bold mb-0 mt-4">HEATING LOADS</h3>
                                    <div className="underline"></div>
                                    <div className="row ml-2 mt-2">
                                        <div className="col-md-6">
                                            <table
                                                className="table table-sm table-bordered table-heating"
                                                data-khipu-role="heating"
                                            >
                                                <thead>
                                                    <tr>
                                                        <th>Section</th>
                                                        <th>Area</th>
                                                        <th>Heat Loss</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr data-khipu-role="template" className="d-none">
                                                        <td className="cc-reference-cell" data-khipu-role="name"></td>
                                                        <td
                                                            data-khipu-role="area"
                                                            data-khipu-format='{ "format": "number", "decimalPlaces": 1 }'
                                                        ></td>
                                                        <td
                                                            data-khipu-role="heatingBTUH"
                                                            data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                        ></td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="col-md-6">
                                            <div
                                                data-coolcalc-role="heating-pie-chart"
                                                className="mt-4 d-flex justify-content-center"
                                            ></div>
                                        </div>
                                    </div>
                                </div>

                                {/* <!-- Cooling --> */}
                                <h3 className="title font-weight-bold mb-0">COOLING LOADS</h3>
                                <div className="underline"></div>
                                <div className="row ml-2 mt-2">
                                    <div className="col-md-6">
                                        <table
                                            className="table table-sm table-bordered table-cooling"
                                            data-khipu-role="cooling"
                                        >
                                            <thead>
                                                <tr>
                                                    <th>Section</th>
                                                    <th>Area</th>
                                                    <th>Sensible</th>
                                                    <th>Latent</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr data-khipu-role="template" className="d-none">
                                                    <td className="cc-reference-cell" data-khipu-role="name"></td>
                                                    <td
                                                        data-khipu-role="area"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 1 }'
                                                    ></td>
                                                    <td
                                                        data-khipu-role="sensibleBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></td>
                                                    <td
                                                        data-khipu-role="latentBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="col-md-6">
                                        <div
                                            data-coolcalc-role="cooling-pie-chart"
                                            className="mt-4 d-flex justify-content-center"
                                        ></div>
                                    </div>
                                </div>

                                {/* <!-- Fenestration loads --> */}
                                <div className="cc-page-break">
                                    <h3 className="title font-weight-bold mb-0">Fenestration loads</h3>
                                    <div className="underline"></div>

                                    <div className="mt-2 ml-2">
                                        <div id="fenestration_messages" className="alert alert-danger d-none"></div>

                                        <div className="card card-body mt-3">
                                            {/* <!-- AED graph. --> */}
                                            <div className="row">
                                                <div className="col-md-10 offset-md-2">
                                                    <div data-coolcalc-role="aed-chart-midsummer"></div>
                                                </div>
                                            </div>
                                            <div className="alert alert-info mt-3" role="alert">
                                                <span>
                                                    This graph represents hourly aggregrate fenestration loads in
                                                    mid-summer.
                                                </span>
                                            </div>
                                        </div>

                                        <div className="card card-body mt-3">
                                            <div className="row">
                                                <div className="col-md-10 offset-md-2">
                                                    <div data-coolcalc-role="aed-chart-october"></div>
                                                </div>
                                            </div>

                                            <div className="alert alert-info mt-3" role="alert">
                                                <span>
                                                    This graph represents hourly aggregrate fenestration loads in
                                                    October.
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div id="cc-component-LC" className="cc-page-break">
                                {/* <!-- Component Loads --> */}
                                <h3 className="title font-weight-bold mb-0 mt-4">COMPONENT LOADS</h3>
                                <div className="underline"></div>

                                {/* <!--Above Grade Walls--> */}
                                <table className="table table-bordered mt-2" data-khipu-role="aboveGradeWalls">
                                    <thead>
                                        <tr>
                                            <th colSpan={4}>Above Grade Walls</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td className="cc-reference-cell">
                                                <p
                                                    className="cc-cell-title font-weight-bold"
                                                    data-khipu-role="title"
                                                ></p>
                                                <small
                                                    className="cc-cell-description"
                                                    data-khipu-role="description"
                                                ></small>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-7">
                                                        <small className="">Construction nr:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-5"
                                                        data-khipu-role="constructionNr"
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-7">
                                                        <small className="">U Value:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-5"
                                                        data-khipu-role="UValue"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 3 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-7">
                                                        <small className="">Exposure:</small>
                                                    </div>
                                                    <div className="col-12 col-md-5" data-khipu-role="exposure"></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-7">
                                                        <small className="">Area:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-5"
                                                        data-khipu-role="area"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 1 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Heating BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="heatingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Cooling BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="coolingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={4} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* <!--Below Grade Walls--> */}
                                <table className="table table-bordered" data-khipu-role="belowGradeWalls">
                                    <thead>
                                        <tr>
                                            <th colSpan={4}>Below Grade Walls</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td className="cc-reference-cell">
                                                <p
                                                    className="cc-cell-title font-weight-bold"
                                                    data-khipu-role="title"
                                                ></p>
                                                <small
                                                    className="cc-cell-description"
                                                    data-khipu-role="description"
                                                ></small>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-7">
                                                        <small className="">Construction nr:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-5"
                                                        data-khipu-role="constructionNr"
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-7">
                                                        <small className="">U Value:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-5"
                                                        data-khipu-role="UValue"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 3 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Area:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="area"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 1 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Heating:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="heatingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={4} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* <!--Ceilings--> */}
                                <table className="table table-bordered" data-khipu-role="ceilings">
                                    <thead>
                                        <tr>
                                            <th colSpan={4}>Ceilings</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td className="cc-reference-cell">
                                                <p
                                                    className="cc-cell-title font-weight-bold"
                                                    data-khipu-role="title"
                                                ></p>
                                                <small
                                                    className="cc-cell-description"
                                                    data-khipu-role="description"
                                                ></small>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-7">
                                                        <small className="">Construction nr:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-5"
                                                        data-khipu-role="constructionNr"
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-7">
                                                        <small className="">U Value:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-5"
                                                        data-khipu-role="UValue"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 3 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Area:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="area"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 1 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Heating BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="heatingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Cooling BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="coolingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={4} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* <!--Skylights--> */}
                                <table className="table table-bordered" data-khipu-role="skylights">
                                    <thead>
                                        <tr>
                                            <th colSpan={4}>Skylights</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td className="cc-reference-cell">
                                                <p
                                                    className="cc-cell-title font-weight-bold"
                                                    data-khipu-role="title"
                                                ></p>
                                                <small
                                                    className="cc-cell-description"
                                                    data-khipu-role="description"
                                                ></small>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Construction nr:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="constructionNr"
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Exposure:</small>
                                                    </div>
                                                    <div className="col-12 col-md-4" data-khipu-role="exposure"></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Area:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="area"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 1 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Heating BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="heatingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Cooling BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="averageCoolingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={4} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div className="alert alert-warning mb-3" role="alert">
                                    <span>
                                        Skylight cooling BTUHs shown here are daily average values. See AED graphs for
                                        details of fenestration loads during the day.
                                    </span>
                                </div>

                                {/* <!--Doors--> */}
                                <table className="table table-bordered" data-khipu-role="doors">
                                    <thead>
                                        <tr>
                                            <th colSpan={4}>Doors</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td className="cc-reference-cell">
                                                <p
                                                    className="cc-cell-title font-weight-bold"
                                                    data-khipu-role="title"
                                                ></p>
                                                <small
                                                    className="cc-cell-description"
                                                    data-khipu-role="description"
                                                ></small>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Construction nr:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="constructionNr"
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">U Value:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="UValue"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 3 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Area:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="area"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 1 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Exposure:</small>
                                                    </div>
                                                    <div className="col-12 col-md-4" data-khipu-role="exposure"></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Heating BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="heatingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Cooling BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="coolingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={4} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* <!--Floors--> */}
                                <table className="table table-bordered" data-khipu-role="floors">
                                    <thead>
                                        <tr>
                                            <th colSpan={4}>Floors</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td className="cc-reference-cell">
                                                <p
                                                    className="cc-cell-title font-weight-bold"
                                                    data-khipu-role="title"
                                                ></p>
                                                <small
                                                    className="cc-cell-description"
                                                    data-khipu-role="description"
                                                ></small>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Construction nr:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="constructionNr"
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Area:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="area"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 1 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-7">
                                                        <small className="">Heating U Value:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-5"
                                                        data-khipu-role="heatingUValue"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 3 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-7">
                                                        <small className="">Cooling U Value:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-5"
                                                        data-khipu-role="coolingUValue"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 3 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Heating BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="heatingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Cooling BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="coolingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">F Value:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="FValue"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 3 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={4} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* <!--Windows--> */}
                                <table className="table table-bordered" data-khipu-role="windows">
                                    <thead>
                                        <tr>
                                            <th colSpan={4}>Windows</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td className="cc-reference-cell">
                                                <p
                                                    className="cc-cell-title font-weight-bold"
                                                    data-khipu-role="title"
                                                ></p>
                                                <small
                                                    className="cc-cell-description"
                                                    data-khipu-role="description"
                                                ></small>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Construction nr:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="constructionNr"
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Area:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="area"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 1 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Exposure:</small>
                                                    </div>
                                                    <div className="col-12 col-md-4" data-khipu-role="exposure"></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-7">
                                                        <small className="">U Value:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-5"
                                                        data-khipu-role="UValue"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 3 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-7">
                                                        <small className="">SHGC:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-5"
                                                        data-khipu-role="SHGC"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 3 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Heating BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="heatingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Cooling BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="averageCoolingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={4} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div className="alert alert-warning mb-3" role="alert">
                                    <span>
                                        Window cooling BTUHs shown here are daily average values. See AED graphs for
                                        details of fenestration loads during the day.
                                    </span>
                                </div>

                                {/* <!--Ventilation--> */}
                                <table className="table table-bordered" data-khipu-role="ventilation">
                                    <thead>
                                        <tr>
                                            <th colSpan={4}>Ventilation</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td className="cc-reference-cell">
                                                <p
                                                    className="cc-cell-title font-weight-bold"
                                                    data-khipu-role="title"
                                                ></p>
                                                <small
                                                    className="cc-cell-description"
                                                    data-khipu-role="description"
                                                ></small>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Heating SER:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="heatSER"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 2 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Sensible SER:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="sensSER"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 2 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Latent LER:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="latLER"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 2 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">CFM:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="CFM"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Heating BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="heatingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Sensible BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="sensibleBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={4} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* <!--Hot water piping--> */}
                                <table className="table table-bordered" data-khipu-role="hotWaterPiping">
                                    <thead>
                                        <tr>
                                            <th colSpan={4}>Hot water piping</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td className="cc-reference-cell">
                                                <p
                                                    className="cc-cell-title font-weight-bold"
                                                    data-khipu-role="title"
                                                ></p>
                                                <small
                                                    className="cc-cell-description"
                                                    data-khipu-role="description"
                                                ></small>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">FValue:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="FValue"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 2 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Length:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="length"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 2 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-8">
                                                        <small className="">Heating BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-4"
                                                        data-khipu-role="heatingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={4} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* <!--Ducts--> */}
                                <table className="table table-bordered" data-khipu-role="ducts">
                                    <thead>
                                        <tr>
                                            <th colSpan={3}>Ducts</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td className="cc-reference-cell">
                                                <p
                                                    className="cc-cell-title font-weight-bold"
                                                    data-khipu-role="title"
                                                ></p>
                                                <small
                                                    className="cc-cell-description"
                                                    data-khipu-role="description"
                                                ></small>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-6 col-md-6">
                                                        <small className="">EHLF:</small>
                                                    </div>
                                                    <div
                                                        className="col-6 col-md-6"
                                                        data-khipu-role="EHLF"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 3 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-6 col-md-6">
                                                        <small className="">ESGF:</small>
                                                    </div>
                                                    <div
                                                        className="col-6 col-md-6"
                                                        data-khipu-role="ESGF"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 3 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-6 col-md-6">
                                                        <small className="">ELG:</small>
                                                    </div>
                                                    <div
                                                        className="col-6 col-md-6"
                                                        data-khipu-role="ELG"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-6">
                                                        <small className="">Heating BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-6"
                                                        data-khipu-role="heatingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-6">
                                                        <small className="">Sensible BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-6"
                                                        data-khipu-role="sensibleBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-6">
                                                        <small className="">Latent BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-6"
                                                        data-khipu-role="latentBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={3} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* <!--Infiltration--> */}
                                <table className="table table-bordered" data-khipu-role="infiltration">
                                    <thead>
                                        <tr>
                                            <th colSpan={3}>Infiltration</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-6">
                                                        <small className="">Leakage Category:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-6"
                                                        data-khipu-role="leakageCategory"
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-6">
                                                        <small className="">NCFM Heating:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-6"
                                                        data-khipu-role="NCFMHeating"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-6">
                                                        <small className="">NCFM Cooling:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-6"
                                                        data-khipu-role="NCFMCooling"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-12 col-md-6">
                                                        <small className="">Heating BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-6"
                                                        data-khipu-role="heatingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-6">
                                                        <small className="">Sensible BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-6"
                                                        data-khipu-role="sensibleBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-6">
                                                        <small className="">Latent BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-12 col-md-6"
                                                        data-khipu-role="latentBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={3} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* <!--Blower Motor--> */}
                                <table className="table table-bordered" data-khipu-role="blowerMotor">
                                    <thead>
                                        <tr>
                                            <th colSpan={3}>Blower Motor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td className="cc-reference-cell">
                                                <p
                                                    className="cc-cell-title font-weight-bold"
                                                    data-khipu-role="title"
                                                ></p>
                                                <small
                                                    className="cc-cell-description"
                                                    data-khipu-role="description"
                                                ></small>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Watts:</small>
                                                    </div>
                                                    <div
                                                        className="col-6"
                                                        data-khipu-role="watts"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Sensible BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-6"
                                                        data-khipu-role="sensibleBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={3} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* <!--Winter Humidification--> */}
                                <table className="table table-bordered" data-khipu-role="winterHumidification">
                                    <thead>
                                        <tr>
                                            <th colSpan={3}>Winter Humidification</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Desired Indoor RH:</small>
                                                    </div>
                                                    <div className="col-6" data-khipu-role="desiredIndoorRH"></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Gallons Per Day:</small>
                                                    </div>
                                                    <div
                                                        className="col-6"
                                                        data-khipu-role="gallonsPerDay"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 1 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Heating BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-6"
                                                        data-khipu-role="heatingBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={3} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* <!--Occupants--> */}
                                <table className="table table-bordered" data-khipu-role="occupants">
                                    <thead>
                                        <tr>
                                            <th colSpan={3}>Occupants</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Nr. Occupants:</small>
                                                    </div>
                                                    <div className="col-6" data-khipu-role="nrOccupants"></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Sensible BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-6"
                                                        data-khipu-role="sensibleBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Latent BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-6"
                                                        data-khipu-role="latentBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={3} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* <!--Appliances--> */}
                                <table className="table table-bordered" data-khipu-role="appliances">
                                    <thead>
                                        <tr>
                                            <th colSpan={3}>Appliances</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td className="cc-reference-cell">
                                                <div className="row">
                                                    <div className="col-12" data-khipu-role="description"></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Quantity:</small>
                                                    </div>
                                                    <div className="col-6" data-khipu-role="quantity"></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Sensible BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-6"
                                                        data-khipu-role="sensibleBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Latent BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-6"
                                                        data-khipu-role="latentBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={3} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* <!--Plants--> */}
                                <table className="table table-bordered" data-khipu-role="plants">
                                    <thead>
                                        <tr>
                                            <th colSpan={3}>Plants</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr data-khipu-role="template" className="d-none">
                                            <td>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Plant Size:</small>
                                                    </div>
                                                    <div className="col-6" data-khipu-role="plantSize"></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Quantity:</small>
                                                    </div>
                                                    <div className="col-6" data-khipu-role="quantity"></div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="row">
                                                    <div className="col-6">
                                                        <small className="">Latent BTUH:</small>
                                                    </div>
                                                    <div
                                                        className="col-6"
                                                        data-khipu-role="latentBTUH"
                                                        data-khipu-format='{ "format": "number", "decimalPlaces": 0 }'
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr data-khipu-role="no-data">
                                            <td colSpan={3} className="cc-table-no-data">
                                                {" "}
                                                There are no components for this section.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* <!-- Room Loads --> */}
                            <div id="cc-room-LC" className="d-none">
                                <div data-khipu-role="roomLoads">
                                    <div className="d-none">
                                        <div id="templateRoomLoads">
                                            <h3 className="title font-weight-bold mb-0 cc-page-break">ROOM DETAIL</h3>
                                            <div className="underline"></div>

                                            <div className="ml-2 mt-2">
                                                <div className="card">
                                                    <div className="card-header">
                                                        <div>
                                                            {" "}
                                                            <b>Room name: </b>{" "}
                                                            <span data-coolcalc-role="roomNameValue"></span>
                                                        </div>
                                                    </div>
                                                    <div className="table-full-width">
                                                        <table className="table">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="text-right">
                                                                        <span className="">Heated square footage</span>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className="badge badge-primary"
                                                                            data-coolcalc-role="heatedFloorArea"
                                                                        ></span>
                                                                    </td>

                                                                    <td className="text-right">
                                                                        <span className="">Total Cooling BTUH</span>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className="badge badge-primary"
                                                                            data-coolcalc-role="coolingValue"
                                                                        ></span>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="text-right">
                                                                        <span className="">Cooled square footage</span>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className="badge badge-primary"
                                                                            data-coolcalc-role="cooledFloorArea"
                                                                        ></span>
                                                                    </td>

                                                                    <td className="text-right">
                                                                        <span className="">Total Heating BTUH</span>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className="badge badge-primary"
                                                                            data-coolcalc-role="heatingValue"
                                                                        ></span>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="text-right">
                                                                        <span className="">
                                                                            Heated volume (above grade CF)
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className="badge badge-primary"
                                                                            data-coolcalc-role="heatedAGV"
                                                                        ></span>
                                                                    </td>

                                                                    <td className="text-right">
                                                                        <span className="">CFM</span>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className="badge badge-primary"
                                                                            data-coolcalc-role="cfmValue"
                                                                        ></span>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="text-right">
                                                                        <span className="">
                                                                            Cooled volume (above grade CF)
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className="badge badge-primary"
                                                                            data-coolcalc-role="cooledAGV"
                                                                        ></span>
                                                                    </td>

                                                                    <td className="text-right">
                                                                        <span className="">Exposed wall area (SF)</span>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className="badge badge-primary"
                                                                            data-coolcalc-role="exposedWallArea"
                                                                        ></span>
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>

                                                <div className="card card-body mt-4">
                                                    <div className="row mt-3">
                                                        <div className="col-md-10 offset-md-2">
                                                            <div data-coolcalc-role="heatingCoolingChartRoom"></div>
                                                        </div>
                                                    </div>
                                                    <div className="row mt-3">
                                                        <div className="col-md-10 offset-md-2">
                                                            <div
                                                                data-coolcalc-role="AEDChartRoomSummer"
                                                                className="w-100"
                                                            ></div>
                                                        </div>
                                                    </div>
                                                    <div className="row mt-3">
                                                        <div className="col-md-10 offset-md-2">
                                                            <div
                                                                data-coolcalc-role="AEDChartRoomOctober"
                                                                className="w-100"
                                                            ></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(withHistory(withDynamicGoogleMaps(CoolCalc)))),
    definition: {
        displayName: "CoolCalc",
        group: "Common",
    },
};

export default widgetModule;
